import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { themeBackgrounds } from '../../theme/theme';
import {
  buildUpdateCustomKitVariantPayload,
  getCustomKitVariant,
  normalizeVariantConfig,
  updateCustomKitVariant,
} from '../../services/customKitVariantService';
import { WidgetBuilderConfig } from '../../types/widgetBuilder';
import LayoutSidebar from '../../components/widget-builder/LayoutSidebar';
import ChatCallPreview from '../../components/widget-builder/ChatCallPreview';
import ChatBotPreview from '../../components/widget-builder/ChatBotPreview';
import EmbedCodePlatformMenu from '../../components/widget-builder/EmbedCodePlatformMenu';
import EmbedCodeIntegrateDialog from '../../components/widget-builder/EmbedCodeIntegrateDialog';
import {
  matchNoCodePlatformId,
  NoCodePlatformOption,
} from '../../components/widget-builder/noCodePlatformVisuals';

const WidgetBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { varientId } = useParams<{ varientId: string }>();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const appName = useSelector((state: any) => state.applicationDetails.app_name);

  const [config, setConfig] = useState<WidgetBuilderConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [isFullscreenPreview, setIsFullscreenPreview] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [embedMenuAnchor, setEmbedMenuAnchor] = useState<HTMLElement | null>(null);
  const [embedPlatformId, setEmbedPlatformId] = useState('wordpress');
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const embedCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFullscreenPreview) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreenPreview(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreenPreview]);

  useEffect(() => {
    const loadVariant = async () => {
      const organizationId = userDetails?.organization_id || '';
      const applicationId = appId || '';
      if (!organizationId || !applicationId || !varientId) {
        setConfig(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const variant = await getCustomKitVariant({
          organization_id: organizationId,
          application_id: applicationId,
          varient_id: varientId,
        });

        if (variant) {
          const normalized = normalizeVariantConfig(variant);
          setConfig(normalized);
          setProjectName(appName || normalized.platform_name || 'Sampark Connect');
          setEmbedPlatformId(matchNoCodePlatformId(normalized.platform_name));
        } else {
          setConfig(null);
          toast.error('Custom kit variant not found.', {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error('Error loading variant for widget builder:', error);
        setConfig(null);
        toast.error('Failed to load variant details.', {
          position: 'top-right',
          autoClose: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadVariant();
  }, [userDetails?.organization_id, appId, varientId, appName]);

  const embedMenuOpen = Boolean(embedMenuAnchor);

  const clearEmbedCloseTimer = () => {
    if (embedCloseTimerRef.current) {
      clearTimeout(embedCloseTimerRef.current);
      embedCloseTimerRef.current = null;
    }
  };

  const openEmbedMenu = (anchor: HTMLElement) => {
    clearEmbedCloseTimer();
    setEmbedMenuAnchor(anchor);
  };

  const scheduleEmbedMenuClose = () => {
    clearEmbedCloseTimer();
    embedCloseTimerRef.current = setTimeout(() => {
      setEmbedMenuAnchor(null);
      embedCloseTimerRef.current = null;
    }, 160);
  };

  useEffect(() => () => clearEmbedCloseTimer(), []);

  const handleEmbedPlatformSelect = (option: NoCodePlatformOption) => {
    setEmbedPlatformId(option.id);
    clearEmbedCloseTimer();
    setEmbedMenuAnchor(null);
    setEmbedDialogOpen(true);
  };

  const embedCredentials = useMemo(
    () => ({
      organizationId: userDetails?.organization_id || '',
      appId: appId || '',
      variantId: config?.varient_id || varientId || '',
    }),
    [userDetails?.organization_id, appId, config?.varient_id, varientId]
  );

  const handleSaveAndPublish = async () => {
    if (!config || isSaving) return;

    const organizationId = userDetails?.organization_id || '';
    const applicationId = appId || '';
    if (!organizationId || !applicationId || !config.varient_id) {
      toast.error('Missing organization, application, or variant details.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildUpdateCustomKitVariantPayload(
        organizationId,
        applicationId,
        config
      );
      await updateCustomKitVariant(payload);
      toast.success('Variant saved and published successfully.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error updating custom kit variant:', error);
      toast.error('Failed to save variant. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const previewTitle = useMemo(() => {
    if (!config) return 'Widget Builder';
    return config.is_bot_varient ? 'Chat Bot Design' : 'Chat / Call Design';
  }, [config]);

  const compactButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: 12.5,
    px: 1.35,
    py: 0.45,
    minHeight: 30,
    borderRadius: 1.5,
    lineHeight: 1.2,
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#F4F7FB',
        }}
      >
        <CircularProgress sx={{ color: '#007AFF' }} />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          backgroundColor: '#F4F7FB',
        }}
      >
        <Typography sx={{ color: '#64748B' }}>Variant not found.</Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#EEF2F7',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          height: 56,
          px: { xs: 1.5, md: 2 },
          flexShrink: 0,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, justifySelf: 'start' }}>
          <IconButton
            onClick={() => navigate(-1)}
            size="small"
            sx={{
              width: 30,
              height: 30,
              border: '1px solid #E2E8F0',
              borderRadius: 1.5,
              backgroundColor: '#F8FAFC',
              '&:hover': { backgroundColor: '#F1F5F9' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box
            component="img"
            src="/images/sampark-logo.png"
            alt="Sampark"
            sx={{ height: 42, width: 'auto', flexShrink: 0, display: 'block' }}
          />
          <Chip
            size="small"
            label="No code"
            sx={{
              height: 20,
              fontWeight: 500,
              fontSize: 10.5,
              backgroundColor: 'rgba(0, 122, 255, 0.1)',
              color: '#007AFF',
              border: '1px solid rgba(0, 122, 255, 0.16)',
              '& .MuiChip-label': { px: 0.9 },
            }}
          />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.45,
            minHeight: 30,
            borderRadius: 999,
            backgroundColor: 'rgba(0, 122, 255, 0.08)',
            maxWidth: 280,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: '#22C55E',
              flexShrink: 0,
            }}
          />
          {isEditingName ? (
            <InputBase
              autoFocus
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  setIsEditingName(false);
                }
              }}
              sx={{
                flex: 1,
                fontSize: 13,
                fontWeight: 600,
                color: '#0F172A',
                '& input': { py: 0, textAlign: 'center' },
              }}
            />
          ) : (
            <Typography
              noWrap
              sx={{
                flex: 1,
                fontSize: 13,
                fontWeight: 600,
                color: '#0F172A',
                textAlign: 'center',
              }}
            >
              {projectName || 'Sampark Connect'}
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={() => setIsEditingName(true)}
            sx={{ width: 22, height: 22, color: '#94A3B8', p: 0 }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flexShrink: 0, justifySelf: 'end' }}
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
            onMouseEnter={(event) => openEmbedMenu(event.currentTarget)}
            onMouseLeave={scheduleEmbedMenuClose}
            onClick={(event) => {
              if (embedMenuOpen) {
                clearEmbedCloseTimer();
                setEmbedMenuAnchor(null);
              } else {
                openEmbedMenu(event.currentTarget);
              }
            }}
            sx={{
              ...compactButtonSx,
              borderColor: embedMenuOpen ? '#007AFF' : '#E2E8F0',
              color: embedMenuOpen ? '#007AFF' : '#475569',
              backgroundColor: '#FFFFFF',
              '& .MuiButton-startIcon': { mr: 0.6 },
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Get embed code
          </Button>
          <EmbedCodePlatformMenu
            open={embedMenuOpen}
            anchorEl={embedMenuAnchor}
            selectedId={embedPlatformId}
            onClose={() => {
              clearEmbedCloseTimer();
              setEmbedMenuAnchor(null);
            }}
            onMouseEnter={clearEmbedCloseTimer}
            onMouseLeave={scheduleEmbedMenuClose}
            onSelect={handleEmbedPlatformSelect}
          />
          <EmbedCodeIntegrateDialog
            open={embedDialogOpen}
            platformId={embedPlatformId}
            credentials={embedCredentials}
            onClose={() => setEmbedDialogOpen(false)}
          />
          <Button
            variant="contained"
            startIcon={
              isSaving ? (
                <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
              ) : (
                <SaveOutlinedIcon sx={{ fontSize: 15 }} />
              )
            }
            disabled={isSaving}
            onClick={handleSaveAndPublish}
            sx={{
              ...compactButtonSx,
              background: themeBackgrounds.default,
              color: '#FFFFFF',
              boxShadow: 'none',
              '& .MuiButton-startIcon': { mr: 0.6 },
              '&:hover': { background: themeBackgrounds.default, opacity: 0.94, boxShadow: 'none' },
              '&.Mui-disabled': {
                background: themeBackgrounds.default,
                color: '#FFFFFF',
                opacity: 0.7,
              },
            }}
          >
            {isSaving ? 'Saving...' : 'Save & Publish'}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '360px minmax(0, 1fr)',
          },
          gridTemplateRows: { xs: 'minmax(420px, 1fr) auto', lg: '1fr' },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRight: { lg: '1px solid rgba(15, 23, 42, 0.08)' },
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            order: { xs: 2, lg: 1 },
            maxHeight: { xs: 340, lg: 'none' },
          }}
        >
          <LayoutSidebar config={config} onChange={setConfig} />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            order: { xs: 1, lg: 2 },
            background: 'linear-gradient(180deg, #F7FAFC 0%, #EEF3F8 100%)',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pt: { xs: 2, md: 2.5 },
              pb: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#007AFF',
                  lineHeight: 1.3,
                  mb: 0.6,
                }}
              >
                Live product preview
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={device}
                onChange={(_, value) => value && setDevice(value)}
                sx={{
                  p: 0.4,
                  backgroundColor: '#FFFFFF',
                  borderRadius: 2.5,
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '10px !important',
                    mx: 0.15,
                    px: 1.5,
                    py: 0.65,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12.5,
                    color: '#64748B',
                  },
                  '& .Mui-selected': {
                    backgroundColor: 'rgba(0,122,255,0.1) !important',
                    color: '#007AFF !important',
                  },
                }}
              >
                <ToggleButton value="desktop">
                  <DesktopWindowsOutlinedIcon sx={{ fontSize: 17, mr: 0.75 }} />
                  Desktop
                </ToggleButton>
                <ToggleButton value="mobile">
                  <PhoneIphoneOutlinedIcon sx={{ fontSize: 17, mr: 0.75 }} />
                  Mobile
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton
                size="small"
                onClick={() => setIsFullscreenPreview(true)}
                sx={{
                  width: 34,
                  height: 34,
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  color: '#64748B',
                  '&:hover': { color: '#007AFF', borderColor: '#007AFF' },
                }}
              >
                <FullscreenIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              px: { xs: 2, md: 3 },
              pb: { xs: 2, md: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                maxWidth: device === 'mobile' ? 400 : '100%',
                borderRadius: 3,
                border: '1px solid rgba(15, 23, 42, 0.06)',
                background:
                  'radial-gradient(circle at 12% 8%, rgba(0,122,255,0.07), transparent 36%), radial-gradient(circle at 88% 12%, rgba(56,189,248,0.08), transparent 32%), #E8EEF5',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 1.5, md: 2.5 },
                overflow: 'hidden',
              }}
            >
              {config.is_bot_varient ? (
                <ChatBotPreview config={config} device={device} />
              ) : (
                <ChatCallPreview config={config} device={device} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {isFullscreenPreview && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            background:
              'radial-gradient(circle at 15% 10%, rgba(0,122,255,0.1), transparent 40%), radial-gradient(circle at 85% 0%, rgba(56,189,248,0.12), transparent 36%), #0B1220',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              height: 64,
              px: { xs: 2, md: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              backgroundColor: 'rgba(15, 23, 42, 0.72)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
                Full Screen Preview
              </Typography>
              <Typography noWrap sx={{ fontSize: 12.5, color: 'rgba(226,232,240,0.8)' }}>
                {config.platform_name} · {previewTitle}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} alignItems="center">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={device}
                onChange={(_, value) => value && setDevice(value)}
                sx={{
                  p: 0.35,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  borderRadius: 2.5,
                  border: '1px solid rgba(255,255,255,0.12)',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '10px !important',
                    mx: 0.1,
                    px: 1.4,
                    py: 0.55,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12.5,
                    color: 'rgba(226,232,240,0.85)',
                  },
                  '& .Mui-selected': {
                    backgroundColor: 'rgba(0,122,255,0.28) !important',
                    color: '#FFFFFF !important',
                  },
                }}
              >
                <ToggleButton value="desktop">
                  <DesktopWindowsOutlinedIcon sx={{ fontSize: 17, mr: 0.75 }} />
                  Desktop
                </ToggleButton>
                <ToggleButton value="mobile">
                  <PhoneIphoneOutlinedIcon sx={{ fontSize: 17, mr: 0.75 }} />
                  Mobile
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="contained"
                startIcon={<CloseIcon />}
                onClick={() => setIsFullscreenPreview(false)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  color: '#0F172A',
                  '&:hover': { backgroundColor: '#F1F5F9' },
                }}
              >
                Exit
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, md: 3.5 },
              overflow: 'auto',
            }}
          >
            {config.is_bot_varient ? (
              <ChatBotPreview config={config} device={device} />
            ) : (
              <ChatCallPreview config={config} device={device} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WidgetBuilder;
